import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import { externalPreload } from '@/utils/dom'
import { Context as GeneralContext } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import MainHero from '@/components/MainHero'
import Footer from '@/components/Footer'
import ProductItem from '@/components/ProductItem'
import Meta from '@/components/Meta'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveProducts = ({
  cpt: cptFromProps,
}) => {
  const { headerHeight, getImageUrl, setBackButtonVisible, setBackButtonLink, enableSingleToScroll, setEnableSingleToScroll } = useContext(GeneralContext)
  const { scroll } = useContext(ScrollbarContext)
  const { setReady, ready } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const [products, setProducts] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const params = useParams()
  const [load, setLoad] = useState(false)
  const $root = useRef()

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { currentCategory, productList, allProducts, strings, mediaQueryKey, scrollOffset } = useSelector((state) => ({
    allProducts: Object.values(state.page.pages) || [],
    currentCategory: state.page.taxonomies[cptFromProps]?.find((tax) => tax.slug === params[cptFromProps]) || {},
    categories: state.shop.categories || [],
    mediaQueryKey: state.mediaQuery.key,
    productList: state.page.archive[params[cptFromProps]] || [],
    strings: state.language.string,
    scrollOffset: state.page.singleScrollOffset,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getProducts = useCallback((cpt, tax, term) => dispatch(actions.page.getListTerms(cpt, tax, term)), [dispatch])
  const getTaxonomiesList = useCallback((data) => dispatch(actions.page.getAllTerms(data)), [dispatch])
  const setSingleScrollOffset = useCallback((num) => dispatch(actions.page.setSingleScrollOffset(num)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image & imageHover
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    products.slice(0, 3).map((item) => {
      preload.current.loadFile({ id: `img-${item.id}`, src: item.image })
      return false
    })
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (productList.length === 0) getProducts('product', cptFromProps, params[cptFromProps])
    if (Object.keys(currentCategory).length === 0) getTaxonomiesList('_cta_collection')
  }

  /*------------------------------
  Add to cart
  ------------------------------*/
  const handleAddToCart = (id, quantity) => {
    if (window.Ecwid) window.Ecwid.Cart.addProduct({ id, quantity })
  }

  /*------------------------------
  Set Product by filtering all products by current slug category
  ------------------------------*/
  useEffect(() => {
    if (productList.length > 0) {
      const mappedProducts = allProducts.filter((p) => productList.includes(p.slug)).map((item) => {
        return ({
          id: item.id,
          title: item.title.rendered,
          slug: item.slug,
          link: item.link,
          image: getImageUrl(item.featured_media, 'large', item.slug),
          isInStock: false,
          quantity: 0,
          attributes: [],
          shoppable: false,
        })
      })
      setProducts(mappedProducts)
    }
  }, [productList])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
    return () => {
      if (scroll.current) setSingleScrollOffset(scroll.current.y)
      setEnableSingleToScroll(false)
    }
  }, [])

  /*------------------------------
  Setting del Back Button
  ------------------------------*/
  useEffect(() => {
    if (load) {
      setBackButtonVisible(true)
      setBackButtonLink(strings.collections_menu_link)
    }
  }, [load])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (products.length > 0) preloadMedia()
  }, [products])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    const title = decodeEntities(currentCategory.yoast_title || '') || decodeEntities(currentCategory.name || '') || ''
    return load && (
      <Meta
        title={title.replace('@', '')}
        meta={[
          { name: 'description', content: currentCategory.yoast_desc || '' },
          { property: 'og:title', content: title },
          { property: 'og:description', content: currentCategory.yoast_desc || '' },
        ]}
      />
    )
  }

  const getCurrentCategoryTitle = () => {
    if (currentCategory.name.includes('@')) return currentCategory.name.split('@')
    if (currentCategory.name !== '') return currentCategory.name?.split(' ')
    return []
  }

  /*------------------------------
  Render Cover
  ------------------------------*/
  const renderCoverProject = () => {
    const coverImgSizes = JSON.parse(currentCategory.meta.taxonomy_featured_image_id_sizes)
    return (
      <>
        <div className={classes.heroContainer}>
          {currentCategory.meta?.taxonomy_featured_image_url && (
            <MainHero
              className={classes.hero}
              title={getCurrentCategoryTitle()}
              img={mediaQueryKey <= 1 ? coverImgSizes.medium[0] : coverImgSizes.full[0]}
              ready={ready}
              scrollIcon
              parallax
              delay={1}
              bigFont
            />
          )}
          <div className={classes.heroFooter}>
            {mediaQueryKey > 1 && (
              <div className={classes.heroText}>
                <p>{currentCategory.description}</p>
              </div>
            )}
            <div className={classes.heroLabels}>
              {currentCategory.meta?.section_title}
            </div>
            <div className={classes.heroLabels}>
              {currentCategory.meta?.year}
            </div>
          </div>
        </div>
        {mediaQueryKey <= 1 && currentCategory.description && (
          <div className={classes.heroText}>
            <p>{currentCategory.description}</p>
          </div>
        )}
      </>
    )
  }

  /*------------------------------
  Render Products
  ------------------------------*/
  const renderProducts = () => {
    const isAlphanumericOrder = currentCategory.meta.alphanumeric_order === 'true'
    return products.length > 0 && (
      <div className={classes.products}>
        <ul className={classes.productView}>
          {products
            .sort((a, b) => {
              if (isAlphanumericOrder && a.title < b.title) return -1
              if (isAlphanumericOrder && a.title > b.title) return 1
              return 0
            })
            .map((product) => (
              <ProductItem
                key={product.id}
                item={product}
                shop={product.eciwd}
                onAddCartClick={() => handleAddToCart(product.id, 1)}
              />
            ))}
        </ul>
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return load && <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverProject()}
        <div className={classes.wrap}>
          <div className={classes.page}>
            {renderProducts()}
          </div>
        </div>
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveProducts ${classes.root}`}
      ref={$root}
      scrollOffset={enableSingleToScroll ? scrollOffset : 0}
    >
      {renderHelmet()}
      {renderContent()}
      {renderFooter()}
    </LocomotiveScroll>
  )
}

export default ArchiveProducts
